import { useTranslation } from 'react-i18next';
/**
 * ActivityLogs — live API.
 * Reads from: GET /v1/system/audit-logs/activity
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Typography, IconButton, Stack, Tooltip, Chip, TextField, TablePagination,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Select, MenuItem, FormControl, InputLabel, CircularProgress, Snackbar,
} from '@mui/material';
import { RefreshOutlined } from '@mui/icons-material';
import { ApiError, systemApi } from '../../../api';
import type { AuditLog } from '../../../api/system';
import { activeLocale } from '../../../utils/datetime';
import { ExportButton } from '../../../components/primitives';
import { translateApiError } from '../../../i18n/translateApiError';

function actionColor(a: string): 'success' | 'error' | 'warning' | 'default' | 'info' {
  const l = a.toLowerCase();
  if (l.includes('create')) return 'success';
  if (l.includes('delete')) return 'error';
  if (l.includes('update')) return 'info';
  if (l.includes('approve')) return 'success';
  if (l.includes('reject')) return 'warning';
  return 'default';
}

/** Humanize a raw resource_type token (path-derived, e.g. 'leave-types' →
 *  'Leave Types') as a fallback when there's no explicit i18n label. */
function humanizeResource(s: string): string {
  return s.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const ActivityLogs: React.FC = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [resourceFilter, setResourceFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await systemApi.auditApi.listActivity();
      setLogs(data);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('system:activityLogs.loadError'));
      setLogs([]);
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const resources = useMemo(() => Array.from(new Set(logs.map(d => d.resource_type).filter(Boolean))), [logs]);

  // Friendly, unambiguous resource label. Explicit i18n overrides for tokens
  // that are confusing raw (e.g. 'devices' = a worker's MOBILE device, not a
  // generic device); humanized fallback for everything else.
  const resourceLabel = useCallback((rt: string | null | undefined): string => {
    if (!rt) return '—';
    return t(`system:activityLogs.resourceLabels.${rt}`, { defaultValue: humanizeResource(rt) });
  }, [t]);

  // Details: the specific entity id when known (parsed from the path tail by
  // the backend audit middleware); else the cleaned endpoint path from the
  // captured `changes` so the row isn't a perpetual "—" (and a /me/ path makes
  // clear it was the worker's own self-service action). Falls back to "—".
  const detailText = useCallback((d: AuditLog): string => {
    if (d.resource_id) return d.resource_id;
    const ch = d.changes as { path?: string } | null | undefined;
    if (ch?.path) return ch.path.replace(/^\/v1\//, '').replace(/^\//, '');
    return '—';
  }, []);

  const filtered = useMemo(() => {
    return logs.filter(d => {
      if (resourceFilter !== 'all' && d.resource_type !== resourceFilter) return false;
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (d.action ?? '').toLowerCase().includes(q)
        || (d.resource_type ?? '').toLowerCase().includes(q)
        || (d.resource_id ?? '').toLowerCase().includes(q)
        || (d.worker_id ?? '').toLowerCase().includes(q);
    });
  }, [logs, resourceFilter, search]);

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Export rows for `ExportButton`. Two real defects were in the hand-rolled version
  // this replaces: it joined cells with `.join(',')` and NO escaping, so any action or
  // resource id containing a comma silently corrupted the file, and it omitted the
  // UTF-8 BOM, so Excel garbled every non-ASCII value. `exportTable` does both.
  //
  // Headers are LOCALISED here, unlike a round-trip import file: an audit log is read
  // by a person in Excel — often an auditor — and never re-uploaded.
  // Reuses `tableHeaders.*` rather than a second set of column labels — the screen and
  // the file must not drift into two names for one column (§10.5b). The export carries
  // two columns the table folds into "details", which is why those two keys were added
  // to the SAME block instead of a new one.
  const exportColumns = useMemo(() => [
    t('system:activityLogs.tableHeaders.timestamp'),
    t('system:activityLogs.tableHeaders.action'),
    t('system:activityLogs.tableHeaders.resource'),
    t('system:activityLogs.tableHeaders.resourceId'),
    t('system:activityLogs.tableHeaders.worker'),
    t('system:activityLogs.tableHeaders.ip'),
  ], [t]);

  const exportRows = useMemo(() => filtered.map((d) => ({
    [exportColumns[0]]: d.created_at ?? '',
    [exportColumns[1]]: d.action ?? '',
    [exportColumns[2]]: d.resource_type ?? '',
    [exportColumns[3]]: d.resource_id ?? '',
    [exportColumns[4]]: d.worker_id ?? '',
    [exportColumns[5]]: d.ip_address ?? '',
  })), [filtered, exportColumns]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('system:activityLogs.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('system:activityLogs.subtitleDetail')}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={t('system:shared.refresh')}><IconButton size="small" onClick={fetchAll} disabled={loading} sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#fff', '&:hover': { bgcolor: '#f5f5f5' } }}><RefreshOutlined fontSize="small" /></IconButton></Tooltip>
          <ExportButton
            filename={`activity-logs-${new Date().toISOString().slice(0, 10)}`}
            title={t('system:activityLogs.title')}
            columns={exportColumns}
            rows={exportRows}
            onDone={(f) => setToast(t('common:export.done', { count: exportRows.length, format: f }))}
            onError={(m) => setToast(m)}
          />
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', mb: 1, gap: 1.5, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('system:activityLogs.resourceLabel')}</InputLabel>
          <Select value={resourceFilter} label={t('system:activityLogs.resourceLabel')} onChange={e => { setResourceFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">{t('system:activityLogs.allResources')}</MenuItem>
            {resources.map(r => <MenuItem key={r} value={r}>{resourceLabel(r)}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" label={t('system:activityLogs.searchLabel')} value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} sx={{ width: 300 }} />
        <Box sx={{ flex: 1 }} />
        <TablePagination component="div" count={filtered.length} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[10, 25, 50]} sx={{ '.MuiTablePagination-toolbar': { pl: 0, minHeight: 36 } }} />
      </Box>

      {loading ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
              <TableRow>
                <TableCell>{t('system:activityLogs.tableHeaders.timestamp')}</TableCell>
                <TableCell>{t('system:activityLogs.tableHeaders.action')}</TableCell>
                <TableCell>{t('system:activityLogs.tableHeaders.resource')}</TableCell>
                <TableCell>{t('system:activityLogs.tableHeaders.details')}</TableCell>
                <TableCell>{t('system:activityLogs.tableHeaders.ip')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                  <Typography variant="body2" color="text.secondary">{search || resourceFilter !== 'all' ? t('system:activityLogs.noMatch') : t('system:activityLogs.noLogs')}</Typography>
                </TableCell></TableRow>
              ) : paginated.map(d => (
                <TableRow key={d.id} hover>
                  <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>{d.created_at ? new Date(d.created_at).toLocaleString(activeLocale()) : '—'}</Typography></TableCell>
                  <TableCell><Chip label={d.action} size="small" color={actionColor(d.action)} variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} /></TableCell>
                  <TableCell><Typography variant="caption" sx={{ fontWeight: 600 }}>{resourceLabel(d.resource_type)}</Typography></TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>{detailText(d)}</Typography></TableCell>
                  <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>{d.ip_address ?? '—'}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </Box>
  );
};

export default ActivityLogs;
